// A site entry for a token (issue 1027), the client's half: "Link to an
// entry…" in the DM's Properties for a token and in the turn tracker for a
// combatant, a small search of the site's entries for the table's system,
// and the linked token's compact stat block — the entry's name linking to its
// site page, then label: value rows, plain text, no prose — with Unlink. A
// player sees the entry's name alone, and only when their projection carries
// it (the token's name shared with them, or theirs). The server reads the
// site and sets the numbers; a refusal is said through the notice as it came.
// Issue 1057 (GM-010): each row of the DM's stat block has a Reveal toggle
// that sends THIS token's whole list of revealed facts (`object.facts`); a
// player who can see the token reads them as "What you know".
import { useEffect, useState } from 'react';
import { NO_STAT_BLOCK_LAYOUT, afterReduction, handEntered, type Values, groupStats, groupSummary, reductionOf, type EntryRef, type Fact, type Inferred, type PanelKind, type StatBlockLayout, type StatGroup, type StatRow, type TableObject } from '@dragons-table/vtt-shared';
import { entryPageUrl, linkCombatantEntry, linkEntry, rollAbilityCheck, searchEntries, statBlock, type StatAbility, type EntryAnswer, type EntryHit, type StatAnswer } from '../../entries';
import { useTableState } from '../../table/TableState';
import { useHolds } from '../../table/mayAct';
import { hpWords, useNumberWords, type NumberWords } from '../../table/numberWords';
import { useShell } from '../context';
import { BTN } from '../ui';
import { usePanelPrefs, useSectionOpen } from '../panelPrefs';

const btn = BTN;
const SEARCH_DELAY_MS = 250;

/** The entry a thing in this person's projection carries: the reference for the DM, `{ name }` for a player, or null. */
export const entryOf = (o: unknown): Partial<EntryRef> & { name: string } | null => {
  const e = (o as { entry?: unknown } | null)?.entry as { name?: unknown } | undefined;
  return e && typeof e.name === 'string' ? (e as Partial<EntryRef> & { name: string }) : null;
};

/** Say what the server said; true when it went through. */
function useSay(): (r: EntryAnswer, ok: string) => boolean {
  const { setNotice } = useTableState();
  return (r, ok) => {
    if (r.kind === 'ok') { setNotice(ok); return true; }
    setNotice(r.kind === 'refused' ? r.reason : 'That could not be done right now.');
    return false;
  };
}

/**
 * The numbers typed on an unlinked token that a link would replace (issue 1612), in the table's words: "HP 12/12, AC 15".
 * Empty when there are none (or the token is linked already): then a link asks nothing.
 */
export function typedNumbers(o: { entry?: unknown; character?: unknown; values?: Values }, w: NumberWords): string {
  if (!o.values) return '';
  const v = o.values;
  const signed = (n: number) => (n < 0 ? `${n}` : `+${n}`);
  return handEntered({ ...o, values: v }).flatMap((k) => {
    if (k === 'hp') return v.hp ? [hpWords(v.hp, w)] : [];
    if (k === 'ac') return [`${w.defence} ${v.ac}`];
    if (k === 'initBonus') return [`${w.initiative} ${signed(v.initBonus!)}`];
    return Object.entries(v.resources ?? {}).map(([key, r]) => `${key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())} ${r.current}${r.max !== undefined ? `/${r.max}` : ''}`);
  }).join(', ');
}

/** The question a link asks before it replaces typed numbers (issue 1612): Replace goes on; Keep mine sends nothing. */
export function ReplaceQuestion({ typed, busy, onReplace, onKeep }: { typed: string; busy: boolean; onReplace: () => void; onKeep: () => void }) {
  return (
    <div role="alertdialog" aria-label="Replace the typed numbers?" className="w-full space-y-1 rounded border border-amber-700 p-1.5" data-testid="link-replace-question">
      <p className="text-amber-200">This replaces the numbers typed on this token: {typed}. Replace them?</p>
      <div className="flex gap-1">
        <button type="button" className={btn} disabled={busy} onClick={onReplace}>Replace</button>
        <button type="button" className={btn} disabled={busy} autoFocus onClick={onKeep}>Keep mine</button>
      </div>
    </div>
  );
}

/** A linked creature's ability checks (issue 1612): one button each, its modifier read from the stat block by the server. */
function AbilityChecks({ token, abilities }: { token: { id: string }; abilities: StatAbility[] }) {
  const { campaignId } = useShell();
  const { scene } = useTableState();
  const say = useSay();
  const [secret, setSecret] = useState(true);
  const [busy, setBusy] = useState(false);
  const roll = async (a: StatAbility) => {
    setBusy(true);
    const r = await rollAbilityCheck(campaignId, token.id, a.key, secret, scene?.id ?? null);
    setBusy(false);
    say(r, `Rolled a ${a.label} check.`);
  };
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="ability-checks">
      <span className="text-vtt-muted">Ability check</span>
      {abilities.map((a) => (
        <button key={a.key} type="button" className={btn} disabled={busy} aria-label={`Roll a ${a.label} check`} onClick={() => void roll(a)}>
          {a.label} {a.modifier < 0 ? `−${-a.modifier}` : `+${a.modifier}`}
        </button>
      ))}
      <label className="flex items-center gap-1 text-vtt-muted"><input type="checkbox" checked={secret} onChange={(e) => setSecret(e.target.checked)} />Private</label>
    </div>
  );
}

/** The search: the DM types, the server answers, the DM picks one. */
export function EntryPicker({ label, onPick, onCancel, busy }: { label: string; onPick: (hit: EntryHit) => void; onCancel: () => void; busy: boolean }) {
  const { campaignId } = useShell();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<EntryHit[] | null>(null);
  useEffect(() => {
    const text = q.trim();
    if (!text) { setHits(null); return undefined; }
    let live = true;
    const timer = setTimeout(() => { void searchEntries(campaignId, text).then((h) => { if (live) setHits(h); }); }, SEARCH_DELAY_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [q, campaignId]);
  return (
    <div className="w-full space-y-1" data-testid="entry-picker">
      <div className="flex items-center gap-1">
        <input aria-label={label} type="search" autoFocus className="min-w-0 flex-1 rounded bg-stone-800 px-2 py-0.5" placeholder="Goblin" maxLength={100} value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={btn} onClick={onCancel}>Cancel</button>
      </div>
      {hits !== null && hits.length === 0 && <p className="text-stone-500" data-testid="no-entries">Nothing found for the table's system.</p>}
      {hits !== null && hits.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded border border-stone-700" aria-label="Entries found">
          {hits.map((h) => (
            <li key={`${h.segment}/${h.slug}`}>
              <button type="button" className="flex w-full justify-between gap-2 px-2 py-0.5 text-left hover:bg-stone-800 disabled:opacity-50" disabled={busy} onClick={() => onPick(h)}>
                <span>{h.name}</span><span className="text-stone-500">{h.family}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A linked entry's stat block, asked once per page (`statBlock` remembers it): undefined while asked. With the token's id
 * (issue 1612, decision A), the block is that token's copy from when it was matched, on the scene the DM is looking at.
 */
export function useStatBlock(entry: Pick<EntryRef, 'segment' | 'slug' | 'id'> | null, objectId?: string | null): StatAnswer | undefined {
  const { campaignId } = useShell();
  const { scene } = useTableState();
  const sceneId = scene?.id ?? null;
  const [block, setBlock] = useState<StatAnswer | undefined>(undefined);
  useEffect(() => {
    let live = true;
    setBlock(undefined);
    if (entry) void statBlock(campaignId, entry, undefined, objectId ? { object: objectId, scene: sceneId } : undefined).then((b) => { if (live) setBlock(b); });
    return () => { live = false; };
  }, [campaignId, entry?.segment, entry?.slug, entry?.id, objectId, sceneId]); // eslint-disable-line react-hooks/exhaustive-deps
  return block;
}

/** The layout a stat block is drawn by (issue 1575): the DM's own regrouping where they made one, else the system's. */
export function useStatLayout(block: StatAnswer | undefined): StatBlockLayout {
  const mine = usePanelPrefs().statBlock;
  return mine ?? (block && block !== 'gone' ? block.layout : undefined) ?? NO_STAT_BLOCK_LAYOUT;
}

/** How many of a stat block's rows the table has been shown (issue 1575's "N of M revealed"), by their labels. */
export function revealedCount(rows: ReadonlyArray<{ label: string }>, facts: ReadonlyArray<{ label: string }> | undefined): number {
  const labels = new Set((facts ?? []).map((f) => f.label));
  return rows.filter((r) => labels.has(r.label)).length;
}

/** One heading of the stat block (issue 1575): gold-dim, its own chevron and one-line summary, open as the layout says. */
function StatGroupView({ g, kind, token, isRevealed, toggle }: { g: StatGroup; kind: PanelKind; token?: { id: string } | undefined; isRevealed: (label: string) => boolean; toggle: (row: StatRow) => void }) {
  const [open, setOpen] = useSectionOpen(kind, `stat:${g.key}`, g.open);
  const id = `stat-${g.key}-${token?.id ?? 'x'}`;
  return (
    <div className="w-full" data-testid={`stat-group-${g.key}`}>
      <div className="flex flex-nowrap items-center gap-1">
        <button type="button" className="flex shrink-0 items-center gap-1 text-left text-[0.65625rem] font-semibold uppercase tracking-[0.08em] text-vtt-gold-dim" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
          <span aria-hidden="true" className="w-2.5 text-vtt-gold">{open ? '▾' : '▸'}</span>{g.label}
        </button>
        {!open && <span className="ml-auto truncate text-[0.6875rem] text-vtt-muted" data-testid={`stat-group-summary-${g.key}`}>{groupSummary(g)}</span>}
      </div>
      <dl id={id} hidden={!open} className={`mb-1.5 ${open ? 'grid' : 'hidden'} ${token ? 'grid-cols-[minmax(4.5rem,38%)_1fr_auto]' : 'grid-cols-[minmax(4.5rem,38%)_1fr]'} items-start gap-x-1.5 gap-y-0.5 text-[0.75rem] text-stone-200`}>
        {g.rows.map((s) => (
          <div key={s.label} className="contents">
            <dt className="text-vtt-muted">{s.label}</dt>
            <dd className="break-words">{s.value}</dd>
            {token && (
              // In a dd of its own: a dl's groups may hold only terms and descriptions, never a bare button. The marker
              // (issue 1541): ◉ revealed, ○ hidden; the press reveals it to the players, or takes it back.
              <dd>
                <button type="button" aria-pressed={isRevealed(s.label)} aria-label={`Reveal ${s.label} to the players`}
                  title={isRevealed(s.label) ? 'Revealed: the players who can see it read this. Press to take it back.' : 'Hidden from the players. Press to reveal it.'}
                  className={`rounded px-1 text-[0.6875rem] leading-4 hover:bg-stone-800 ${isRevealed(s.label) ? 'text-vtt-gold' : 'text-vtt-muted'}`} onClick={() => toggle(s)}>
                  {isRevealed(s.label) ? '◉' : '○'}
                </button>
              </dd>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * A linked entry's stat block, read through the server once per page. The page link is the address the server found
 * the entry at NOW (issue 1043: a slug the content moved); an entry no longer on the site is said so, with no link,
 * and the token keeps its numbers — nothing here touches them. Its rows sit under the system's headings (issue 1575),
 * or the DM's own regrouping; whatever no heading places is "Other", never dropped.
 */
export function EntryStatBlock({ entry, token, kind = 'monster' }: { entry: EntryRef; token?: { id: string; facts?: Fact[] }; kind?: PanelKind }) {
  const { act } = useTableState();
  const revealed = token?.facts ?? [];
  const isRevealed = (label: string) => revealed.some((f) => f.label === label);
  // The whole list each time (issue 1057): a reveal appends the row as shown here; an unreveal drops it by its label.
  const toggle = (row: StatRow) => {
    if (!token) return;
    const facts = isRevealed(row.label) ? revealed.filter((f) => f.label !== row.label) : [...revealed, { label: row.label, value: row.value }];
    act({ kind: 'object.facts', id: token.id, facts });
  };
  const block = useStatBlock(entry, token?.id);
  const layout = useStatLayout(block);
  const found = block && block !== 'gone' ? block : null;
  const groups = found ? groupStats(found.stats, layout) : [];
  const shown = found ? revealedCount(found.stats, revealed) : 0;
  return (
    <div className="w-full space-y-1" data-testid="entry-stat-block">
      {block === 'gone'
        ? <span className="font-medium text-stone-300">{entry.name}</span>
        : <a className="font-medium text-amber-300 underline" href={entryPageUrl(found ? found.entry : entry)} target="_blank" rel="noopener noreferrer">{entry.name}</a>}
      {block === undefined && <p className="text-stone-500">Loading…</p>}
      {block === null && <p className="text-amber-300">The entry can't be read from the site just now.</p>}
      {block === 'gone' && <p className="text-amber-300" data-testid="entry-gone">No longer on the site. The token keeps its numbers.</p>}
      {found && token && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-stone-500">Revealed facts show to everyone who can see this token. <span data-testid="revealed-count">{shown} of {found.stats.length} revealed</span>.</p>
          {shown < found.stats.length && <button type="button" className={btn} onClick={() => act({ kind: 'object.facts', id: token.id, facts: found.stats.map((r) => ({ label: r.label, value: r.value })) })}>Reveal all</button>}
          {revealed.length > 0 && <button type="button" className={btn} onClick={() => act({ kind: 'object.facts', id: token.id, facts: [] })}>Reveal none</button>}
        </div>
      )}
      {found?.abilities && token && <AbilityChecks token={token} abilities={found.abilities} />}
      {groups.map((g) => <StatGroupView key={g.key} g={g} kind={kind} token={token} isRevealed={isRevealed} toggle={toggle} />)}
    </div>
  );
}

/**
 * How a token's entry shows in Properties (issue 1612, Tom's A): `full` is the link, stat block and Unlink together;
 * `match` the DM's *Match to a creature* checkbox at the top (ticked opens the search, unticked unlinks); `block` the
 * Stat block section under it, the block and Unlink without a second way to link.
 */
export type LinkMode = 'full' | 'match' | 'block';

/** The Properties panel's block for a token: the DM's link, stat block and Unlink; a player's name of the entry when they were told it. */
export function TokenEntry({ o, kind = 'monster', mode = 'full' }: { o: TableObject | { id: string; kind: string; entry?: { name: string } }; kind?: PanelKind; mode?: LinkMode }) {
  const { campaignId } = useShell();
  const { viewingAs, scene } = useTableState();
  // The entries' routes are the `tokens` power's (issue 1160).
  const isDm = useHolds()('tokens') && !viewingAs;
  const entry = entryOf(o);
  const say = useSay();
  const words = useNumberWords();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  // The entry a link waits on while the DM is asked whether to replace the typed numbers (issue 1612).
  const [asking, setAsking] = useState<EntryHit | null>(null);
  useEffect(() => { setChoosing(false); setAsking(null); }, [o.id]);
  if (o.kind !== 'token') return null;
  if (!isDm) return entry && mode !== 'match' ? <p className="text-xs text-stone-400" data-testid="token-entry">{entry.name}</p> : null;
  const sceneId = scene?.id ?? null;
  const run = async (target: Pick<EntryRef, 'segment' | 'slug'> | null, ok: string, replace = false) => {
    setBusy(true);
    const r = await (replace ? linkEntry(campaignId, o.id, target, sceneId, undefined, true) : linkEntry(campaignId, o.id, target, sceneId));
    setBusy(false);
    setAsking(null);
    if (say(r, ok)) setChoosing(false);
  };
  const typed = typedNumbers(o as { values?: Values }, words);
  const pick = (h: EntryHit) => { if (typed) setAsking(h); else void run(h, `Linked to ${h.name}.`); };
  const ref = entry && entry.segment && entry.slug && entry.system ? (entry as EntryRef) : null;
  const unlink = () => void run(null, 'The token no longer carries an entry.');
  const searching = !ref && choosing && !asking && <EntryPicker label={mode === 'match' ? 'Search the creatures' : "Search the site's entries"} busy={busy} onCancel={() => setChoosing(false)} onPick={pick} />;
  const question = !ref && asking && <ReplaceQuestion typed={typed} busy={busy} onReplace={() => void run(asking, `Linked to ${asking.name}.`, true)} onKeep={() => { setAsking(null); setChoosing(false); }} />;
  if (mode === 'match') {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="match-creature">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={ref !== null || choosing} disabled={busy}
            onChange={(e) => { if (e.target.checked) setChoosing(true); else if (ref) unlink(); else { setChoosing(false); setAsking(null); } }} />
          Match to a creature
        </label>
        {ref && <span className="truncate text-vtt-gold" data-testid="matched-creature">{ref.name}</span>}
        {searching}
        {question}
      </div>
    );
  }
  if (mode === 'block' && !ref) return <p className="text-xs text-vtt-muted" data-testid="token-entry">Not matched to a creature. Tick <em>Match to a creature</em> above.</p>;
  return (
    <div className="flex flex-wrap items-start gap-2 text-xs" data-testid="token-entry">
      {ref && <EntryStatBlock entry={ref} token={o as { id: string; facts?: Fact[] }} kind={kind} />}
      {ref && <button type="button" className={btn} disabled={busy} onClick={unlink}>Unlink the entry</button>}
      {!ref && !choosing && <button type="button" className={btn} onClick={() => setChoosing(true)}>Link to an entry…</button>}
      {searching}
      {question}
    </div>
  );
}

/** The turn tracker's link for a combatant: its tokens carry the entry, so it needs a token on the board. */
export function CombatantEntry({ c, objects }: { c: { id: string; name: string; tokens: string[] }; objects: Record<string, TableObject> }) {
  const { campaignId } = useShell();
  const { scene } = useTableState();
  const say = useSay();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const onBoard = c.tokens.map((id) => objects[id]).filter((o): o is TableObject => !!o && o.kind === 'token');
  if (!onBoard.length) return null;
  const first = onBoard[0]!.entry;
  const shared = first && onBoard.every((o) => o.entry?.slug === first.slug && o.entry?.segment === first.segment) ? first : null;
  const run = async (target: Pick<EntryRef, 'segment' | 'slug'> | null, ok: string) => {
    setBusy(true);
    const r = await linkCombatantEntry(campaignId, c.id, target, scene?.id ?? null);
    setBusy(false);
    if (say(r, ok)) setChoosing(false);
  };
  return (
    <div className="flex w-full flex-wrap items-center gap-1 text-[0.6875rem]" data-testid={`entry-link-${c.id}`}>
      {shared && <a className="text-amber-300 underline" href={entryPageUrl(shared)} target="_blank" rel="noopener noreferrer">{shared.name}</a>}
      {shared && <button type="button" className={btn} disabled={busy} aria-label={`Unlink the entry from ${c.name}`} onClick={() => void run(null, `${c.name} no longer carries an entry.`)}>Unlink</button>}
      {!shared && !choosing && <button type="button" className={btn} aria-label={`Link ${c.name} to an entry`} onClick={() => setChoosing(true)}>Link to an entry…</button>}
      {!shared && choosing && <EntryPicker label={`Search the site's entries for ${c.name}`} busy={busy} onCancel={() => setChoosing(false)} onPick={(h) => void run(h, `${c.name} linked to ${h.name}.`)} />}
    </div>
  );
}

/** What a player knows of a token they can see (issue 1057): the facts the DM revealed of it, label: value; nothing when none. */
/**
 * What players worked out of a creature (issue 1133), as lines: "AC 18–19 (from 2 attacks)", "AC at least 18",
 * "AC at most 19", "Damage dealt: 23 (about 13 after DR 5)". The system's main defence is named in its own word
 * (issue 1309); another defence by the name its rules gave it. Only what this viewer was sent: none without `inferred`.
 */
export function inferredLines(inf: Inferred | undefined, facts: Fact[] | undefined, words: NumberWords, mainKey: string | null): string[] {
  if (!inf) return [];
  const out: string[] = [];
  for (const [key, d] of Object.entries(inf.defences)) {
    const name = key === mainKey ? words.defence : d.label;
    const from = `(from ${d.from} attack${d.from === 1 ? '' : 's'})`;
    if (d.low !== null && d.high !== null) out.push(d.low === d.high ? `${name} ${d.low} ${from}` : `${name} ${d.low}–${d.high} ${from}`);
    else if (d.low !== null) out.push(`${name} at least ${d.low}`);
    else if (d.high !== null) out.push(`${name} at most ${d.high}`);
  }
  // A player's copy carries the count and the estimate; the DM's, each hit (issue 1133).
  const hits = inf.damage.count ?? inf.damage.hits.length;
  if (hits) {
    const dr = reductionOf(facts);
    const after = inf.damage.afterDr ?? (dr !== null && inf.damage.hits.length ? { dr, amount: afterReduction(inf.damage, dr) } : null);
    out.push(`Damage dealt: ${inf.damage.dealt}${after ? ` (about ${after.amount} after DR ${after.dr})` : ''}`);
  }
  return out;
}

/** `inferredLines` in the table's words, for its main defence. */
export function useInferredLines(o: { facts?: Fact[]; inferred?: Inferred } | undefined): string[] {
  const words = useNumberWords();
  const { systemRules } = useTableState();
  return inferredLines(o?.inferred, o?.facts, words, systemRules.attack?.main ?? null);
}

export function KnownFacts({ o }: { o: { facts?: Fact[]; inferred?: Inferred } }) {
  const facts = o.facts ?? [];
  const worked = useInferredLines(o);
  if (!facts.length && !worked.length) return null;
  return (
    <div className="space-y-0.5 text-xs" data-testid="known-facts">
      <p className="font-medium text-stone-300">What you know</p>
      <ul className="space-y-0.5 text-stone-300">
        {facts.map((f) => <li key={f.label} className="break-words"><span className="text-stone-500">{f.label}:</span> {f.value}</li>)}
        {worked.map((l) => <li key={l} className="break-words" data-testid="inferred-line">{l}</li>)}
      </ul>
    </div>
  );
}
